import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { fetchGames, fetchGame, fetchMatchGames, fetchScoringAverage, fetchSegmentWinRate, createGame, updateGame, updateRoundSettings, upsertScore, deleteScore, updateScoreTaken, fetchPresses, createPress, deletePress, createSettlements, deleteGameSettlements, deleteGame, resumeGame } from '../api/games'
import { enqueueScore, cacheGameData, getCachedGameData, cacheQueryData, getCachedQueryData } from '../utils/scoreQueue'
import logger from '../utils/logger'

export function useGames(status) {
  const cacheKey = `games_${status || 'all'}`
  return useQuery({
    queryKey: ['games', status],
    queryFn: async () => {
      const data = await fetchGames(status)
      cacheQueryData(cacheKey, data)
      return data
    },
    initialData: () => getCachedQueryData(cacheKey),
    initialDataUpdatedAt: 0, // always stale → refetch in background
  })
}

export function useGame(id) {
  return useQuery({
    queryKey: ['game', id],
    queryFn: async () => {
      const data = await fetchGame(id)
      cacheGameData(id, data)
      return data
    },
    enabled: !!id,
    initialData: () => getCachedGameData(id),
    initialDataUpdatedAt: 0,
  })
}

export function useMatchGames(matchId) {
  const cacheKey = `matchGames_${matchId}`
  return useQuery({
    queryKey: ['matchGames', matchId],
    queryFn: async () => {
      const data = await fetchMatchGames(matchId)
      cacheQueryData(cacheKey, data)
      return data
    },
    enabled: !!matchId,
    initialData: () => (matchId ? getCachedQueryData(cacheKey) : undefined),
    initialDataUpdatedAt: 0,
  })
}

export function useScoringAverage(playerId) {
  const cacheKey = `scoringAverage_${playerId}`
  return useQuery({
    queryKey: ['scoringAverage', playerId],
    queryFn: async () => {
      const data = await fetchScoringAverage(playerId)
      cacheQueryData(cacheKey, data)
      return data
    },
    enabled: !!playerId,
    initialData: () => (playerId ? getCachedQueryData(cacheKey) : undefined),
    initialDataUpdatedAt: 0,
  })
}

export function useSegmentWinRate(playerId) {
  const cacheKey = `segmentWinRate_${playerId}`
  return useQuery({
    queryKey: ['segmentWinRate', playerId],
    queryFn: async () => {
      const data = await fetchSegmentWinRate(playerId)
      cacheQueryData(cacheKey, data)
      return data
    },
    enabled: !!playerId,
    initialData: () => (playerId ? getCachedQueryData(cacheKey) : undefined),
    initialDataUpdatedAt: 0,
  })
}

export function useCreateGame() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: createGame,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['games'] })
      // Also invalidate the parent match so its rounds list refreshes
      if (data.match_id) {
        queryClient.invalidateQueries({ queryKey: ['match', data.match_id] })
        queryClient.invalidateQueries({ queryKey: ['matches'] })
      }
    },
  })
}

export function useUpdateGame() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, ...updates }) => updateGame(id, updates),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['games'] })
      queryClient.invalidateQueries({ queryKey: ['game', variables.id] })
      // Also invalidate parent match so round status updates on the match page
      if (data?.match_id) {
        queryClient.invalidateQueries({ queryKey: ['match', data.match_id] })
        queryClient.invalidateQueries({ queryKey: ['matches'] })
        queryClient.invalidateQueries({ queryKey: ['matchGames', data.match_id] })
      }
    },
    onError: (error) => {
      logger.error('Game update failed: ' + error?.message)
      alert('Failed to update round. Check your connection and try again.')
    },
  })
}

export function useUpdateRoundSettings() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ gameId, ...settings }) => updateRoundSettings(gameId, settings),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['games'] })
      queryClient.invalidateQueries({ queryKey: ['game', data.id] })
      if (data?.match_id) {
        queryClient.invalidateQueries({ queryKey: ['match', data.match_id] })
        queryClient.invalidateQueries({ queryKey: ['matches'] })
        queryClient.invalidateQueries({ queryKey: ['matchGames', data.match_id] })
      }
    },
  })
}

export function useUpsertScore() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (variables) => {
      // Best-effort direct save — sync engine will retry if this fails
      return upsertScore(variables).catch(() => {})
    },
    onMutate: async (variables) => {
      // Persist to localStorage queue immediately (survives app kill)
      enqueueScore({
        type: 'upsert',
        game_id: variables.game_id,
        player_id: variables.player_id,
        hole_number: variables.hole_number,
        strokes: variables.strokes,
      })

      // Cancel any in-flight refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey: ['game', variables.game_id] })

      // getQueryData returns undefined when only placeholderData exists (e.g. after
      // force-kill + reload offline). Fall back to the localStorage game cache.
      const old = queryClient.getQueryData(['game', variables.game_id])
        ?? getCachedGameData(variables.game_id)
      if (!old) return

      const scores = old.scores || []
      const idx = scores.findIndex(
        (s) => s.player_id === variables.player_id && s.hole_number === variables.hole_number
      )
      const newScore = {
        ...(idx >= 0 ? scores[idx] : {}),
        game_id: variables.game_id,
        player_id: variables.player_id,
        hole_number: variables.hole_number,
        strokes: variables.strokes,
      }
      const newScores = idx >= 0
        ? scores.map((s, i) => (i === idx ? newScore : s))
        : [...scores, newScore]
      const updated = { ...old, scores: newScores }
      queryClient.setQueryData(['game', variables.game_id], updated)
      // Also persist to localStorage so it survives another force-kill
      cacheGameData(variables.game_id, updated)
    },
    // No onError rollback — score is safely in the localStorage queue
    // No onSettled invalidation — sync engine invalidates on successful sync
  })
}

export function useDeleteScore() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (variables) => {
      return deleteScore(variables).catch(() => {})
    },
    onMutate: async (variables) => {
      enqueueScore({
        type: 'delete',
        game_id: variables.game_id,
        player_id: variables.player_id,
        hole_number: variables.hole_number,
      })

      await queryClient.cancelQueries({ queryKey: ['game', variables.game_id] })

      const old = queryClient.getQueryData(['game', variables.game_id])
        ?? getCachedGameData(variables.game_id)
      if (!old) return

      const scores = (old.scores || []).filter(
        (s) => !(s.player_id === variables.player_id && s.hole_number === variables.hole_number)
      )
      const updated = { ...old, scores }
      queryClient.setQueryData(['game', variables.game_id], updated)
      cacheGameData(variables.game_id, updated)
    },
    // No onError rollback — score delete is safely in the localStorage queue
    // No onSettled invalidation — sync engine invalidates on successful sync
  })
}

export function useUpdateScoreTaken() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (variables) => {
      return updateScoreTaken(variables).catch(() => {})
    },
    onMutate: async (variables) => {
      enqueueScore({
        type: 'score_taken',
        game_id: variables.game_id,
        player_id: variables.player_id,
        hole_number: variables.hole_number,
        score_taken: variables.taken,
      })

      await queryClient.cancelQueries({ queryKey: ['game', variables.game_id] })

      const old = queryClient.getQueryData(['game', variables.game_id])
        ?? getCachedGameData(variables.game_id)
      if (!old) return

      const scores = (old.scores || []).map((s) => {
        if (s.player_id === variables.player_id && s.hole_number === variables.hole_number) {
          return { ...s, score_taken: variables.taken }
        }
        return s
      })
      const updated = { ...old, scores }
      queryClient.setQueryData(['game', variables.game_id], updated)
      cacheGameData(variables.game_id, updated)
    },
    // No onError rollback — safely in localStorage queue
    // No onSettled invalidation — sync engine handles it
  })
}

// ============================================
// Press hooks
// ============================================

export function usePresses(gameId) {
  const cacheKey = `presses_${gameId}`
  return useQuery({
    queryKey: ['presses', gameId],
    queryFn: async () => {
      const data = await fetchPresses(gameId)
      cacheQueryData(cacheKey, data)
      return data
    },
    enabled: !!gameId,
    initialData: () => (gameId ? getCachedQueryData(cacheKey) : undefined),
    initialDataUpdatedAt: 0,
  })
}

export function useCreatePress() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: createPress,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['presses', variables.game_id] })
      queryClient.invalidateQueries({ queryKey: ['game', variables.game_id] })
      // Also refresh MoneyBoard data (uses matchGames which embeds bets)
      queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] === 'matchGames' })
    },
  })
}

export function useDeletePress() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ pressId }) => deletePress(pressId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['presses', variables.gameId] })
      queryClient.invalidateQueries({ queryKey: ['game', variables.gameId] })
      // Also refresh MoneyBoard data (uses matchGames which embeds bets)
      queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] === 'matchGames' })
    },
  })
}

// ============================================
// Settlement generation hooks
// ============================================

export function useCreateSettlements() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ gameId, settlements }) => createSettlements(gameId, settlements),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settlements'] })
    },
  })
}

export function useDeleteGame() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ gameId }) => deleteGame(gameId),
    onSuccess: (_data, { matchId }) => {
      queryClient.invalidateQueries({ queryKey: ['games'] })
      queryClient.invalidateQueries({ queryKey: ['matches'] })
      queryClient.invalidateQueries({ queryKey: ['match', matchId] })
      queryClient.invalidateQueries({ queryKey: ['settlements'] })
    },
  })
}

export function useDeleteGameSettlements() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: deleteGameSettlements,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settlements'] })
    },
  })
}

export function useResumeGame() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: resumeGame,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['games'] })
      queryClient.invalidateQueries({ queryKey: ['game', data.id] })
      queryClient.invalidateQueries({ queryKey: ['settlements'] })
      if (data?.match_id) {
        queryClient.invalidateQueries({ queryKey: ['match', data.match_id] })
        queryClient.invalidateQueries({ queryKey: ['matches'] })
        queryClient.invalidateQueries({ queryKey: ['matchGames', data.match_id] })
      }
    },
  })
}
